<script setup lang="ts">
/**
 * Copies `value` to the clipboard. The icon turns into a check and the label
 * into „Kopiert“ for a moment; a polite live region announces it. With
 * `iconOnly` it is a square icon button whose accessible name is the label.
 *
 * Emits `copied` on success and `error` when the clipboard refused — the
 * caller decides whether to show a toast. The value stays on screen next to
 * the button, so a refused copy can still be done by hand.
 */
import Check from 'lucide-vue-next/dist/esm/icons/check.js'; import Copy from 'lucide-vue-next/dist/esm/icons/copy.js';
import { onBeforeUnmount, ref } from 'vue';
import { NxButton, type NxButtonSize, type NxButtonVariant } from '../Button';
import { NxIconButton } from '../IconButton';
import { copyToClipboard } from './clipboard';

const props = withDefaults(
  defineProps<{
    /** Text that lands in the clipboard. */
    value: string;
    label?: string;
    copiedLabel?: string;
    /** Square icon button; `label` becomes its accessible name and tooltip. */
    iconOnly?: boolean;
    height?: NxButtonSize;
    variant?: NxButtonVariant;
    disabled?: boolean;
    /** How long the „copied“ state stays, in ms. */
    resetAfter?: number;
  }>(),
  {
    label: 'Kopieren',
    copiedLabel: 'Kopiert',
    iconOnly: false,
    height: 's',
    variant: 'outline',
    disabled: false,
    resetAfter: 1600,
  }
);

const emit = defineEmits<{
  copied: [value: string];
  error: [];
}>();

const copied = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;

async function copy() {
  if (props.disabled) return;
  const ok = await copyToClipboard(props.value);
  if (!ok) {
    emit('error');
    return;
  }
  copied.value = true;
  emit('copied', props.value);
  clearTimeout(timer);
  timer = setTimeout(() => (copied.value = false), props.resetAfter);
}

onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <span class="nx-copy-button" :class="{ 'is-copied': copied }">
    <NxIconButton
      v-if="props.iconOnly"
      :height="props.height"
      :variant="props.variant === 'outline' ? 'ghost' : props.variant"
      :disabled="props.disabled"
      :title="copied ? props.copiedLabel : props.label"
      :aria-label="props.label"
      data-testid="nx-copy-button"
      @click="copy"
    >
      <Check v-if="copied" class="nx-copy-button__icon nxt-icon-sm" aria-hidden="true" />
      <Copy v-else class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
    <NxButton
      v-else
      :height="props.height"
      :variant="props.variant"
      :disabled="props.disabled"
      data-testid="nx-copy-button"
      @click="copy"
    >
      <Check v-if="copied" class="nx-copy-button__icon nxt-icon-sm" aria-hidden="true" />
      <Copy v-else class="nxt-icon-sm" aria-hidden="true" />
      {{ copied ? props.copiedLabel : props.label }}
    </NxButton>
    <span class="nx-copy-button__live" aria-live="polite">
      {{ copied ? props.copiedLabel : '' }}
    </span>
  </span>
</template>

<style scoped>
.nx-copy-button {
  display: inline-flex;
}
.nx-copy-button__icon {
  color: var(--nxt-success-subtle-fg);
  animation: nx-copy-pop var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-copy-button__live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
@keyframes nx-copy-pop {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-copy-button__icon {
    animation: none;
  }
}
</style>
